import React, { useEffect, useReducer, useRef, useState } from "react";

import { Input, InputChangeEvent, SwitchChangeEvent } from "@progress/kendo-react-inputs";
import { getInitialState } from "@selas/state-management";
import { EntityEditor, handleChange, IAddEntityScreenProps, IEditEntityScreenProps, YesNoSwitch } from "@selas/ui-components";
import { isNullOrEmpty } from "@selas/utils";
import { useTranslation } from "react-i18next";

import Endpoint from "../../../services/api/endpoint";
import { contractTypeReducer } from "../../../state/reducers";
import { IContractType, IDegree } from "../../../utils/types/models";

const ContractTypeEditor: React.FC<IAddEntityScreenProps<IContractType> | IEditEntityScreenProps<IContractType>> = (
	props: IAddEntityScreenProps<IContractType> | IEditEntityScreenProps<IContractType>
) => {
	const { t } = useTranslation();

	const [state, dispatch] = useReducer(contractTypeReducer, getInitialState<IContractType>());
	const initialContractType: IContractType = {
		id: (props as IEditEntityScreenProps<IContractType>).recordId || 0,
		name: "",
		active: true
	};
	const [contractType, setContractType] = useState<IContractType>(initialContractType);
	const [dataChanged, setDataChanged] = useState<boolean>(false);
	const firstField: React.MutableRefObject<Input> = useRef();

	useEffect(() => {
		if (state.entity) {
			setContractType(state.entity);
		}
	}, [state.entity]);

	function onChange(event: InputChangeEvent | SwitchChangeEvent): void {
		setContractType(handleChange(contractType, event));
		setDataChanged(true);
	}

	function getErrorMessages(): string[] {
		const messages: string[] = [];
		if (isNullOrEmpty(contractType.name)) {
			messages.push(t("fill_in_required_field", { field: t("name").toLowerCase() }));
		}
		return messages;
	}

	function onActionButtonClicked(close: boolean, record: IContractType): void {
		if (record && !close) {
			setContractType(record);
			setDataChanged(false);
		}
		props.actionButtonClicked(close, record);
	}

	const readonly: boolean = (props as IEditEntityScreenProps<IDegree>).readonly;

	return (
		<EntityEditor
			width="70%"
			record={contractType}
			endpoint={Endpoint.ContractTypes}
			entityState={state}
			entityType={t("contractTypes")}
			dispatch={dispatch}
			dataChanged={dataChanged}
			readonly={readonly}
			recordName={contractType.name}
			actionButtonClicked={onActionButtonClicked}
			getErrorMessages={getErrorMessages}
			firstFieldRef={firstField}
		>
			<div className="k-form">
				<label className="k-form-field">
					<span>{t("name")} *</span>
					<Input name="name" ref={firstField} value={contractType.name} onChange={onChange} required disabled={readonly} />
				</label>
				{!props.hideActive && (
					<div className="k-form-field">
						<div>{t("active")}</div>
						<YesNoSwitch name="active" checked={contractType.active} onChange={onChange} disabled={readonly} />
					</div>
				)}
			</div>
		</EntityEditor>
	);
};

export default ContractTypeEditor;
